import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence, PanInfo, useMotionValue, animate, useDragControls } from "framer-motion";
import { cn } from "../utils/cn";

export interface MobileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  // optional title shown on the left of the header (grabber extends from title to right)
  title?: string;
  // footer content that stays fixed at the bottom of the sheet (not scrollable)
  footer?: React.ReactNode;
  // snap points as percentages (e.g., [30, 80] means 30% and 80% of viewport height)
  snapPoints?: number[];
  // default snap point index (0-based) or "max" for the largest snap point
  defaultSnap?: number | "max";
  // close thresholds
  closeThreshold?: {
    distancePx?: number; // pixels dragged down to trigger close
    progress?: number; // progress (0-1) of drag to trigger close
    velocity?: number; // velocity threshold to trigger close
  };
  // body scroll lock
  lockBodyScroll?: boolean;
  // overlay tap to close
  overlayClosable?: boolean;
  // show grabber handle at top (default: false)
  showGrabber?: boolean;
  // disable drag-to-close functionality (default: false)
  disableDrag?: boolean;
  // bottom offset to account for bottom app bar (default: 80px + safe area)
  bottomOffset?: string;
  // overlay bottom offset to exclude bottom app bar area
  overlayBottomOffset?: string;
  // full modal mode - blocks everything including bottom bar (default: false)
  fullModal?: boolean;
  // show a close button in the header (useful when drag is disabled)
  showCloseButton?: boolean;
  // keep Radix from auto-focusing the first focusable element on open —
  // on mobile, auto-focusing an input pops the OS keyboard over the sheet
  preventAutoFocus?: boolean;
  // callbacks
  onClose?: () => void;
  onSnapChange?: (activeSnap: number) => void;
  // styling
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  // max height when no snap points (as percentage or px string)
  maxHeight?: string | number;
  // size the sheet to its content with maxHeight as the cap (ignored with snap points)
  fitContent?: boolean;
  // reports whether the content overflows and scrolls. An IosHapticOverlay has
  // to step aside there: on iOS a pan that starts on it never scrolls.
  onScrollableChange?: (scrollable: boolean) => void;
}

// a downward, mostly vertical pull on content that was already at its top
// drags the sheet instead of scrolling
function isSheetPull(deltaX: number, deltaY: number, startScrollTop: number, scrollTop: number): boolean {
  return startScrollTop <= 0 && scrollTop <= 0 && deltaY > 5 && Math.abs(deltaX) < deltaY;
}

// How many sheets are open. Floating chrome (the mobile sitebot chip) stacks
// above a non-modal sheet and would cover its footer actions, so it reads this
// and steps aside.
let openSheetCount = 0;
const openSheetListeners = new Set<() => void>();

function changeOpenSheetCount(delta: number): void {
  openSheetCount += delta;
  openSheetListeners.forEach((listener) => listener());
}

function subscribeToOpenSheets(listener: () => void): () => void {
  openSheetListeners.add(listener);
  return () => {
    openSheetListeners.delete(listener);
  };
}

// true while at least one MobileSheet is open anywhere on the page
export function useAnyMobileSheetOpen(): boolean {
  return React.useSyncExternalStore(subscribeToOpenSheets, () => openSheetCount > 0, () => false);
}

const MobileSheet = React.forwardRef<
  HTMLDivElement,
  MobileSheetProps
>(({
  open,
  onOpenChange,
  children,
  title,
  footer,
  snapPoints,
  defaultSnap = "max",
  closeThreshold = {
    distancePx: 100,
    progress: 0.3,
    velocity: 500,
  },
  lockBodyScroll = true,
  overlayClosable = true,
  showGrabber = false,
  disableDrag = false,
  bottomOffset,
  overlayBottomOffset,
  fullModal = false,
  showCloseButton = false,
  preventAutoFocus = false,
  onClose,
  onSnapChange,
  className,
  overlayClassName,
  contentClassName,
  maxHeight = "80vh",
  fitContent = false,
  onScrollableChange,
}, ref) => {
  const [activeSnapIndex, setActiveSnapIndex] = React.useState<number>(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isDraggingDown, setIsDraggingDown] = React.useState(false);
  const constraintsRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  // the content mounts inside the portal a render late, so effects key off state
  const [contentEl, setContentEl] = React.useState<HTMLDivElement | null>(null);
  const pointerStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const startScrollTopRef = React.useRef<number>(0);
  const dragStartedRef = React.useRef(false);
  // a pull-down claimed as a sheet drag; its touchmoves get cancelled
  const dragClaimedRef = React.useRef(false);
  const dragControls = useDragControls();
  const viewportHeight = React.useMemo(() => {
    if (typeof window !== "undefined") {
      return window.innerHeight;
    }
    return 800; // fallback
  }, []);

  // calculate snap positions
  const snapPositions = React.useMemo(() => {
    if (!snapPoints || snapPoints.length === 0) {
      // single height mode - no snap points
      const height = typeof maxHeight === "number"
        ? `${maxHeight}px`
        : typeof maxHeight === "string" && maxHeight.includes("px")
          ? maxHeight
          : typeof maxHeight === "string" && maxHeight.includes("vh")
            ? `${(parseFloat(maxHeight) / 100) * viewportHeight}px`
            : `${(80 / 100) * viewportHeight}px`;
      const heightPx = parseFloat(height);
      return {
        positions: [0], // fully open
        heights: [heightPx],
        maxHeightPx: heightPx,
      };
    }

    // sort snap points descending (largest first)
    const sorted = [...snapPoints].sort((a, b) => b - a);
    const maxHeightPx = (sorted[0] / 100) * viewportHeight;
    const positions: number[] = [];
    const heights: number[] = [];

    sorted.forEach((snap) => {
      const heightPx = (snap / 100) * viewportHeight;
      const position = maxHeightPx - heightPx; // translateY offset
      positions.push(position);
      heights.push(heightPx);
    });

    return {
      positions,
      heights,
      maxHeightPx,
    };
  }, [snapPoints, maxHeight, viewportHeight]);

  // determine initial snap index
  React.useEffect(() => {
    if (snapPositions.positions.length === 0) return;

    if (defaultSnap === "max") {
      setActiveSnapIndex(0); // first (largest) snap point
    } else if (typeof defaultSnap === "number" && defaultSnap < snapPositions.positions.length) {
      setActiveSnapIndex(defaultSnap);
    } else {
      setActiveSnapIndex(0);
    }
  }, [defaultSnap, snapPositions.positions.length]);

  // body scroll lock
  React.useEffect(() => {
    if (!lockBodyScroll) return;

    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open, lockBodyScroll]);

  React.useEffect(() => {
    if (!open) return;
    changeOpenSheetCount(1);
    return () => changeOpenSheetCount(-1);
  }, [open]);

  // motion values for drag
  const y = useMotionValue(snapPositions.maxHeightPx);
  const isClosingRef = React.useRef(false);

  // animate y when activeSnapIndex changes (programmatic) or when sheet opens/closes
  React.useEffect(() => {
    if (isClosingRef.current) {
      // don't animate if we're in the process of closing via drag
      return;
    }

    if (!open) {
      // animate to closed position when closing
      if (disableDrag) {
        animate(y, snapPositions.maxHeightPx, { type: "spring", stiffness: 320, damping: 34 });
      } else {
        animate(y, snapPositions.maxHeightPx, { duration: 0.3, ease: "easeOut" });
      }
    } else if (open && !isDragging && snapPositions.positions.length > 0) {
      // animate to target position when opening or snapping
      const targetY = snapPositions.positions[activeSnapIndex] || 0;
      if (disableDrag) {
        animate(y, targetY, { type: "spring", stiffness: 320, damping: 34 });
      } else {
        animate(y, targetY, { duration: 0.3, ease: "easeOut" });
      }
    }
  }, [activeSnapIndex, isDragging, snapPositions.positions, snapPositions.maxHeightPx, y, open, disableDrag]);

  // handle drag - track direction for elastic behavior
  const handleDrag = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (disableDrag) return;
    const currentY = y.get();
    const startY = snapPositions.positions[activeSnapIndex] || 0;
    const draggedDown = currentY > startY;
    setIsDraggingDown(draggedDown);
  };

  // handle drag end
  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (disableDrag) return;

    const currentY = y.get();
    const velocity = info.velocity.y;
    const distance = info.offset.y;
    const startY = snapPositions.positions[activeSnapIndex] || 0;
    const draggedDown = currentY > startY;

    // lower thresholds for more responsive closing
    const distanceThreshold = closeThreshold.distancePx || 50; // reduced from 100
    const progressThreshold = closeThreshold.progress || 0.2; // reduced from 0.3
    const velocityThreshold = closeThreshold.velocity || 300; // reduced from 500

    // check if should close (dragged down beyond threshold)
    const shouldClose =
      (draggedDown && distance > 0 && distance > distanceThreshold) ||
      (draggedDown && currentY > startY + (snapPositions.maxHeightPx * progressThreshold)) ||
      (velocity > 0 && velocity > velocityThreshold);

    if (shouldClose) {
      isClosingRef.current = true;
      setIsDragging(false);
      setIsDraggingDown(false);
      // animate to closed position before closing - smooth without bounce
      if (disableDrag) {
        animate(y, snapPositions.maxHeightPx, { type: "spring", stiffness: 320, damping: 34 }).then(() => {
          onClose?.();
          onOpenChange(false);
          isClosingRef.current = false;
        });
      } else {
        animate(y, snapPositions.maxHeightPx, { duration: 0.25, ease: [0.4, 0, 0.2, 1] }).then(() => {
          onClose?.();
          onOpenChange(false);
          isClosingRef.current = false;
        });
      }
      return;
    }

    // find nearest snap point
    if (snapPositions.positions.length > 0) {
      let nearestIndex = 0;
      let nearestDistance = Math.abs(currentY - snapPositions.positions[0]);

      snapPositions.positions.forEach((pos, index) => {
        const dist = Math.abs(currentY - pos);
        if (dist < nearestDistance) {
          nearestDistance = dist;
          nearestIndex = index;
        }
      });

      const targetY = snapPositions.positions[nearestIndex];
      setIsDragging(false);
      setIsDraggingDown(false);
      // animate to nearest snap point
      const animationPromise = disableDrag
        ? animate(y, targetY, { type: "spring", stiffness: 320, damping: 34 })
        : animate(y, targetY, { duration: 0.3, ease: "easeOut" });
      animationPromise.then(() => {
        setActiveSnapIndex(nearestIndex);
        onSnapChange?.(nearestIndex);
      });
    } else {
      setIsDragging(false);
      setIsDraggingDown(false);
    }
  };

  const dragStartYRef = React.useRef<number>(0);

  const handleDragStart = () => {
    if (disableDrag) return;
    setIsDragging(true);
    dragStartYRef.current = y.get();
  };

  // handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (overlayClosable && e.target === e.currentTarget) {
      onClose?.();
      onOpenChange(false);
    }
  };

  const setContentRef = React.useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
    setContentEl(node);
  }, []);

  React.useEffect(() => {
    if (!contentEl || !onScrollableChange) return;
    const report = () => onScrollableChange(contentEl.scrollHeight > contentEl.clientHeight + 1);
    report();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(report);
    observer.observe(contentEl);
    Array.from(contentEl.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [contentEl, onScrollableChange]);

  // A pull-down that becomes a sheet drag must not also start a native pan:
  // iOS would cancel the pointer mid-drag and the sheet would twitch and snap
  // back. Cancelling the touchmoves needs a non-passive native listener, and
  // it decides from touch coordinates so it doesn't depend on whether the
  // pointermove or the touchmove for a frame arrives first.
  React.useEffect(() => {
    if (!contentEl || disableDrag) return;
    let start: { x: number; y: number; scrollTop: number } | null = null;
    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      start = touch ? { x: touch.clientX, y: touch.clientY, scrollTop: contentEl.scrollTop } : null;
      dragClaimedRef.current = false;
    };
    const onTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!dragClaimedRef.current && start && touch) {
        dragClaimedRef.current = isSheetPull(
          touch.clientX - start.x,
          touch.clientY - start.y,
          start.scrollTop,
          contentEl.scrollTop,
        );
      }
      if (dragClaimedRef.current && e.cancelable) e.preventDefault();
    };
    const onTouchEnd = () => {
      start = null;
      dragClaimedRef.current = false;
    };
    contentEl.addEventListener("touchstart", onTouchStart, { passive: true });
    contentEl.addEventListener("touchmove", onTouchMove, { passive: false });
    contentEl.addEventListener("touchend", onTouchEnd);
    contentEl.addEventListener("touchcancel", onTouchEnd);
    return () => {
      contentEl.removeEventListener("touchstart", onTouchStart);
      contentEl.removeEventListener("touchmove", onTouchMove);
      contentEl.removeEventListener("touchend", onTouchEnd);
      contentEl.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [contentEl, disableDrag]);

  // handle pointer events on scrollable content to enable drag-to-close when at top
  const handleContentPointerDown = (e: React.PointerEvent) => {
    if (disableDrag || !contentRef.current) return;
    if (e.pointerType === "touch") {
      pointerStartRef.current = { x: e.clientX, y: e.clientY };
      startScrollTopRef.current = contentRef.current.scrollTop;
      dragStartedRef.current = false;
    }
  };

  const handleContentPointerMove = (e: React.PointerEvent) => {
    if (disableDrag) return;
    if (!contentRef.current || pointerStartRef.current === null || e.pointerType !== "touch") return;
    if (dragStartedRef.current || isDragging) return;

    const deltaX = e.clientX - pointerStartRef.current.x;
    const deltaY = e.clientY - pointerStartRef.current.y;

    if (isSheetPull(deltaX, deltaY, startScrollTopRef.current, contentRef.current.scrollTop)) {
      dragStartedRef.current = true;
      dragClaimedRef.current = true;
      e.preventDefault();
      dragControls.start(e.nativeEvent);
    }
  };

  const handleContentPointerUp = () => {
    pointerStartRef.current = null;
    startScrollTopRef.current = 0;
    dragStartedRef.current = false;
  };

  const sheetHeight = snapPositions.maxHeightPx;
  const autoHeight = fitContent && !(snapPoints && snapPoints.length > 0);

  // compute offsets and z-index based on fullModal mode
  const effectiveBottomOffset = bottomOffset ?? (
    fullModal
      ? "env(safe-area-inset-bottom, 0px)"
      : "calc(80px + env(safe-area-inset-bottom, 0px))"
  );
  const effectiveOverlayBottomOffset = overlayBottomOffset ?? (
    fullModal
      ? "0px"
      : "calc(80px + env(safe-area-inset-bottom, 0px))"
  );
  const zIndex = fullModal ? "z-[1010]" : "z-[70]";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={fullModal}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            {/* custom backdrop - excludes bottom menu bar area in default mode */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={cn(
                "fixed left-0 right-0 top-0 bg-black/80 backdrop-blur-sm",
                zIndex,
                overlayClassName
              )}
              style={{
                bottom: effectiveOverlayBottomOffset,
                // iOS ignores the body overflow lock, so a drag on the dimmed
                // area would otherwise scroll the page behind the sheet
                touchAction: "none",
              }}
              onClick={handleOverlayClick}
            />

            {/* sheet content */}
            <DialogPrimitive.Content
              asChild
              forceMount
              ref={constraintsRef}
              onOpenAutoFocus={preventAutoFocus ? (e) => e.preventDefault() : undefined}
            >
              <motion.div
                ref={ref}
                drag={disableDrag ? false : "y"}
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{
                  top: 0,
                  bottom: snapPositions.maxHeightPx + 50, // allow dragging beyond for closing
                }}
                dragElastic={disableDrag ? false : { top: 0, bottom: isDraggingDown ? 0 : 0.1 }}
                dragMomentum={false}
                onDragStart={handleDragStart}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
                style={{
                  y,
                  height: autoHeight ? "auto" : sheetHeight,
                  maxHeight: sheetHeight,
                  bottom: effectiveBottomOffset,
                  touchAction: disableDrag ? "auto" : "none",
                }}
                className={cn(
                  "fixed left-0 right-0",
                  zIndex,
                  "bg-paper border-brutal border-ink",
                  "rounded-t-[12px]",
                  "shadow-brutal-lg",
                  "overflow-hidden",
                  "flex flex-col",
                  className
                )}
              >
                {/* header area with optional title, grabber, and close button */}
                {(showGrabber || title || showCloseButton) && (
                  <div
                    className={cn(
                      "flex-shrink-0 flex items-center",
                      title ? "justify-between px-3 pt-2 pb-1" : "justify-center px-4 pt-3 pb-2",
                      !disableDrag && "cursor-grab active:cursor-grabbing"
                    )}
                    style={{ touchAction: disableDrag ? "auto" : "none" }}
                    onPointerDown={(e) => {
                      if (!disableDrag) {
                        e.preventDefault();
                        dragControls.start(e.nativeEvent);
                      }
                    }}
                  >
                    {title ? (
                      <>
                        <h2 className="text-sm font-medium text-gray-500">
                          {title}
                        </h2>
                        <div className="flex items-center gap-2">
                          {showGrabber && (
                            <div className="w-12 h-1.5 bg-ink/30 rounded-full" />
                          )}
                          {showCloseButton && (
                            <button
                              type="button"
                              onClick={() => { onClose?.(); onOpenChange(false); }}
                              className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                              aria-label="Close"
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-gray-600">
                                <path d="M1 1L11 11M1 11L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        {showGrabber && (
                          <div className="w-12 h-1.5 bg-ink/30 rounded-full" />
                        )}
                        {showCloseButton && (
                          <button
                            type="button"
                            onClick={() => { onClose?.(); onOpenChange(false); }}
                            className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                            aria-label="Close"
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-gray-600">
                              <path d="M1 1L11 11M1 11L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* scrollable content */}
                <div
                  ref={setContentRef}
                  className={cn(
                    // flex-auto in fit mode: a 0% basis in an auto-height
                    // column collapses the content on older Safari
                    autoHeight ? "flex-auto min-h-0" : "flex-1",
                    // hitting either end must not scroll the page behind
                    "overflow-y-auto overscroll-y-contain",
                    "px-4",
                    footer ? "pb-4" : "pb-[calc(1rem+env(safe-area-inset-bottom,0px))]",
                    contentClassName
                  )}
                  style={{
                    // always allow scrolling - drag is controlled via dragControls
                    touchAction: "pan-y",
                  }}
                  onPointerDown={handleContentPointerDown}
                  onPointerMove={handleContentPointerMove}
                  onPointerUp={handleContentPointerUp}
                  onPointerCancel={handleContentPointerUp}
                >
                  {children}
                </div>

                {/* fixed footer - stays at bottom, not scrollable */}
                {footer && (
                  <div className="flex-shrink-0">
                    {footer}
                  </div>
                )}
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
});

MobileSheet.displayName = "MobileSheet";

export { MobileSheet };
